/** A period in which a preacher cannot serve; `from` and `to` are YYYY-MM-DD, inclusive. */
export interface Absence {
    id: string;
    from: string;
    to: string;
    note: string;
}

/** Absences per preacher key. */
export type AbsenceMap = Map<string, Absence[]>;

/** A local date as YYYY-MM-DD (service dates are local calendar days in the app). */
export function toIsoDay(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** The absence of a preacher that covers a day, if any. ISO dates compare as strings. */
export function findAbsence(absences: AbsenceMap, preacherKey: string | undefined | null, day: string | Date): Absence | undefined {
    if (!preacherKey) return undefined;
    const iso = typeof day === "string" ? day : toIsoDay(day);
    return absences.get(preacherKey)?.find((absence) => absence.from <= iso && iso <= absence.to);
}

/** "01.11.2026 – 07.11.2026", or a single date for a one-day absence. */
export function formatAbsence(absence: Pick<Absence, "from" | "to">, locale: string): string {
    const format = (iso: string) =>
        new Date(`${iso}T00:00:00`).toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
    return absence.from === absence.to ? format(absence.from) : `${format(absence.from)} – ${format(absence.to)}`;
}

/** How often each preacher serves: in the quarter and in the year of `now`. */
export interface Workload {
    quarter: number;
    year: number;
}

/**
 * Counts assignments per preacher. A service counts once per slot the preacher
 * holds; services outside the current year are ignored.
 */
export function countWorkload(
    services: { date: Date; sermon1PreacherKey?: string; sermon2PreacherKey?: string }[],
    now: Date
): Map<string, Workload> {
    const result = new Map<string, Workload>();
    const quarter = Math.floor(now.getMonth() / 3);
    for (const service of services) {
        if (service.date.getFullYear() !== now.getFullYear()) continue;
        const inQuarter = Math.floor(service.date.getMonth() / 3) === quarter;
        for (const key of [service.sermon1PreacherKey, service.sermon2PreacherKey]) {
            if (!key) continue;
            const entry = result.get(key) ?? { quarter: 0, year: 0 };
            entry.year++;
            if (inQuarter) entry.quarter++;
            result.set(key, entry);
        }
    }
    return result;
}
